import { useEffect } from 'react';
import { observer } from 'mobx-react';

import { useCollectionPreviewStore } from '@/collection/stores/CollectionPreviewStore';
import DriveItemPreview from '@/drive/components/DriveItemPreview/DriveItemPreview';
import { DriveItemType } from '@/drive/entities/DriveItem/DriveItem';
import PinnedItemsStackPreview from '@/forwarding/components/PinnedItemsStackPreview/PinnedItemsStackPreview';
import { ForwardPreviewData } from '@/forwarding/types/ForwardPreviewData';
import MediaPreview from '@/media/components/MediaPreview/MediaPreview';
import { MediaSize } from '@/media/constants/sizeCodes';
import { MEDIA_SIZE_MAP } from '@/media/constants/sizeCodeToPx';

export interface ForwardPreviewProps {
  preview: ForwardPreviewData;
  mediaSize: MediaSize;
}

function ForwardPreview(props: ForwardPreviewProps) {
  const { preview, mediaSize } = props;

  const { ensureFresh } = useCollectionPreviewStore();

  useEffect(() => {
    const collectionId =
      preview.type === DriveItemType.Collection || preview.type === DriveItemType.CollectionSubscription
        ? preview.collectionId
        : undefined;

    if (collectionId) {
      ensureFresh([collectionId]);
    }
  }, [preview, ensureFresh]);

  if (preview.type === 'component') {
    const { component: PreviewComponent } = preview;

    return <PreviewComponent mediaSize={mediaSize} />;
  }

  if (preview.type === 'productEntity') {
    const media = preview.product?.defaultMedia;

    return (
      <MediaPreview
        media={media}
        mediaSize={mediaSize}
        mediaSource={media?.mainMediaSource}
        product={preview.product}
      />
    );
  }

  if (preview.type === 'pinnedItems') {
    return <PinnedItemsStackPreview preview={preview} size={MEDIA_SIZE_MAP[mediaSize]} />;
  }

  return <DriveItemPreview preview={preview} size={MEDIA_SIZE_MAP[mediaSize]} />;
}

export default observer(ForwardPreview);
